/**
 * /vault — DFY Producer Vault.
 *
 * Two states, both transcribed from the monolith: the locked upsell panel for
 * accounts without the `dfyVault` entitlement, and the four-tab browser (Overview,
 * Favorites, Downloads, Recently Used), each tab now in components/vault/. The pack
 * detail drawer renders over the top — see components/vault/VaultPackDrawer.tsx.
 *
 * SIMULATED. Packs are bundled fixtures and "download" writes a text manifest.
 * The lock below is presentation, NOT access control: the entitlement flag is
 * client-side and editable from devtools. Phase 4 makes it server-authoritative.
 */
import { Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Btn, LoadError, LoadSkeleton, SectionHeading, TabBar, UpgradePrompt } from '../components/ui';
import type { Tab } from '../components/ui';
import { JVZOO_PRODUCT_URLS } from '../config';
import {
  VaultDownloadsTab,
  VaultFavoritesTab,
  VaultOverviewTab,
  VaultPackDrawer,
  VaultRecentTab,
} from '../components/vault';
import { formatUpgradeOfferPrice, upgradeOffer } from '../data/upgradeOffers';
import { useEntitlements, useVault } from '../hooks';
import { openExternalUrl } from '../lib/externalNavigation';
import type { VaultTab } from '../stores/vaultStore';
import { c } from '../styles/theme';
import type { VaultPack } from '../types/domain';
import { paths } from './paths';

const TABS: Tab<VaultTab>[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'favorites', label: 'Favorites' },
  { key: 'downloads', label: 'Downloads' },
  { key: 'recent', label: 'Recently Used' },
];

export default function Vault() {
  const navigate = useNavigate();
  const { dfyVault } = useEntitlements();
  const vault = useVault();

  /*
    The upsell's "150+ prompts" style figure. Kept as a plain expression: it reads
    off the same fixtures the summary cards use, and the locked view is the one
    place that needs the prompt count before `useVault`'s totals are relevant.
  */
  const vaultOffer = upgradeOffer('dfy-producer-vault');

  if (!dfyVault) {
    return (
      <div>
        <SectionHeading
          as="h1"
          title="DFY Producer Vault"
          subtitle="A premium resource vault of prompt packs, genre presets, structures and hooks."
          icon={<Package size={17} color={c.accent} />}
          marginBottom={18}
        />
        <UpgradePrompt
          paddingY={28}
          icon={<Package size={28} color={c.text3} />}
          title="Included with the DFY Producer Vault plan"
          description="Ready-made prompts, templates and producer workflows to speed up music creation and release"
          action={
            <Btn
              href={JVZOO_PRODUCT_URLS['dfy-producer-vault']}
              pendingOnNavigate
              onClick={() => openExternalUrl(JVZOO_PRODUCT_URLS['dfy-producer-vault'])}
            >
              <Package size={13} />
              {`Get ${vaultOffer?.product.name} — ${formatUpgradeOfferPrice(vaultOffer?.product ?? { price: 37 })}`}
            </Btn>
          }
        />
      </div>
    );
  }

  /** Opens a pack in the drawer. */
  const open = (pack: VaultPack) => {
    void vault.openPack(pack.id);
  };

  // Bound to a local so the drawer's callbacks close over a definitely-non-null
  // pack instead of needing a `!` on every one of them.
  const activePack = vault.activePack;

  if (vault.load.status === 'loading' && vault.packs.length === 0) {
    return <LoadSkeleton rows={3} rowHeight={112} label="Loading vault packs…" />;
  }

  if (vault.load.status === 'error' && vault.packs.length === 0) {
    return <LoadError message={vault.load.error?.message ?? 'Could not load vault packs.'} onRetry={vault.loadVaultData} />;
  }

  return (
    <div>
      <SectionHeading
        as="h1"
        title="DFY Producer Vault"
        subtitle="Browse curated collections and bring them straight into your Create workflow."
        icon={<Package size={17} color={c.accent} />}
        marginBottom={14}
      />

      <TabBar
        tabs={TABS}
        active={vault.tab}
        onSelect={vault.setTab}
        label="Vault sections"
        marginBottom={18}
      />

      {vault.tab === 'overview' && <VaultOverviewTab vault={vault} onOpenPack={open} />}
      {vault.tab === 'favorites' && <VaultFavoritesTab vault={vault} onOpenPack={open} />}
      {vault.tab === 'downloads' && <VaultDownloadsTab vault={vault} />}
      {vault.tab === 'recent' && <VaultRecentTab vault={vault} onOpenPack={open} />}

      {activePack && (
        <VaultPackDrawer
          pack={activePack}
          resourceTab={vault.resourceTab}
          onSelectResourceTab={vault.setResourceTab}
          onClose={vault.closePack}
          favorited={vault.favoritePacks.includes(activePack.id)}
          onToggleFavorite={() => vault.togglePackFavorite(activePack.id)}
          favoriteItems={vault.favoriteItems}
          onToggleItemFavorite={vault.toggleItemFavorite}
          onDownload={() => vault.downloadPack(activePack)}
          onCopy={vault.copyText}
          onUsePrompt={(prompt) => {
            vault.applyPrompt(activePack, prompt);
            navigate(paths.create);
          }}
          onLoadPreset={(preset) => {
            vault.applyPreset(activePack, preset);
            navigate(paths.create);
          }}
        />
      )}
    </div>
  );
}
